import { useState, useEffect } from "react";
import axios from "axios";
import { toast } from "sonner";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Card, CardContent, CardHeader, CardTitle, CardDescription } from "@/components/ui/card";
import { Badge } from "@/components/ui/badge";
import { Plus, Trash2, Loader2, User, ChevronLeft, ChevronRight, Check } from "lucide-react";
import { format, startOfMonth, endOfMonth, startOfWeek, addDays, isSameMonth, isSameDay } from "date-fns";
import { de, enUS } from "date-fns/locale";

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

export default function ExperimenterTab({ language, onDataChange }) {
  const dateLocale = language === "de" ? de : enUS;
  const [experimenters, setExperimenters] = useState([]);
  const [loading, setLoading] = useState(true);
  const [adding, setAdding] = useState(false);

  // Form
  const [name, setName] = useState("");
  const [maxHours, setMaxHours] = useState("8");
  const [sessionDur, setSessionDur] = useState("3");

  // Availability calendar
  const now = new Date();
  const [calYear, setCalYear] = useState(now.getFullYear());
  const [calMonth, setCalMonth] = useState(now.getMonth() + 1);
  const [selectedExpId, setSelectedExpId] = useState(null);
  const [availDates, setAvailDates] = useState({}); // {date_str: {start_time, end_time}}
  const [savingAvail, setSavingAvail] = useState(false);
  const [defaultStart, setDefaultStart] = useState("09:00");
  const [defaultEnd, setDefaultEnd] = useState("17:00");

  const monthNames = language === "de"
    ? ["Januar","Februar","März","April","Mai","Juni","Juli","August","September","Oktober","November","Dezember"]
    : ["January","February","March","April","May","June","July","August","September","October","November","December"];
  const dayHeaders = language === "de" ? ["Mo","Di","Mi","Do","Fr","Sa","So"] : ["Mon","Tue","Wed","Thu","Fri","Sat","Sun"];

  useEffect(() => { fetchData(); }, []); // eslint-disable-line react-hooks/exhaustive-deps
  useEffect(() => { if (selectedExpId) loadAvailability(); }, [selectedExpId, calYear, calMonth]); // eslint-disable-line react-hooks/exhaustive-deps

  const fetchData = async () => {
    setLoading(true);
    try {
      const res = await axios.get(`${API}/admin/experimenters`, { withCredentials: true });
      setExperimenters(res.data);
      if (res.data.length > 0 && !selectedExpId) setSelectedExpId(res.data[0].id);
    } catch (e) { toast.error("Error"); }
    finally { setLoading(false); }
  };

  const loadAvailability = async () => {
    try {
      const res = await axios.get(`${API}/admin/availability/${selectedExpId}/${calYear}/${calMonth}`, { withCredentials: true });
      const map = {};
      for (const entry of res.data) {
        map[entry.date] = { start_time: entry.start_time, end_time: entry.end_time };
      }
      setAvailDates(map);
    } catch (e) { console.error(e); }
  };

  const handleAdd = async () => {
    if (!name.trim()) return;
    setAdding(true);
    try {
      const res = await axios.post(`${API}/admin/experimenters`, {
        name: name.trim(), max_weekly_hours: parseFloat(maxHours), session_duration_hours: parseFloat(sessionDur)
      }, { withCredentials: true });
      toast.success(language === "de" ? "Hinzugefügt" : "Added");
      setName(""); setMaxHours("8"); setSessionDur("3");
      fetchData();
      setSelectedExpId(res.data.id);
      if (onDataChange) onDataChange();
    } catch (e) { toast.error("Error"); }
    finally { setAdding(false); }
  };

  const handleDelete = async (id) => {
    try {
      await axios.delete(`${API}/admin/experimenters/${id}`, { withCredentials: true });
      toast.success(language === "de" ? "Gelöscht" : "Deleted");
      if (selectedExpId === id) setSelectedExpId(null);
      fetchData();
      if (onDataChange) onDataChange();
    } catch (e) { toast.error("Error"); }
  };

  const toggleDate = (dateStr) => {
    setAvailDates(prev => {
      const copy = { ...prev };
      if (copy[dateStr]) { delete copy[dateStr]; }
      else { copy[dateStr] = { start_time: defaultStart, end_time: defaultEnd }; }
      return copy;
    });
  };

  const saveAvailability = async () => {
    if (!selectedExpId) return;
    setSavingAvail(true);
    try {
      const entries = Object.entries(availDates).map(([date, times]) => ({
        date, start_time: times.start_time, end_time: times.end_time
      }));
      await axios.post(`${API}/admin/availability`, {
        experimenter_id: selectedExpId, year: calYear, month: calMonth, entries
      }, { withCredentials: true });
      toast.success(`${entries.length} ${language === "de" ? "Tage gespeichert" : "days saved"}`);
      if (onDataChange) onDataChange();
    } catch (e) { toast.error("Error"); }
    finally { setSavingAvail(false); }
  };

  const prevMonth = () => { if (calMonth === 1) { setCalMonth(12); setCalYear(y => y - 1); } else setCalMonth(m => m - 1); };
  const nextMonth = () => { if (calMonth === 12) { setCalMonth(1); setCalYear(y => y + 1); } else setCalMonth(m => m + 1); };

  const buildWeeks = () => {
    const first = new Date(calYear, calMonth - 1, 1);
    const last = endOfMonth(first);
    const start = startOfWeek(first, { weekStartsOn: 1 });
    const weeks = [];
    let cur = start;
    while (cur <= last || weeks.length < 5) {
      const week = [];
      for (let i = 0; i < 7; i++) { week.push(new Date(cur)); cur = addDays(cur, 1); }
      weeks.push(week);
      if (cur > addDays(last, 7)) break;
    }
    return weeks;
  };

  const selectedExp = experimenters.find(e => e.id === selectedExpId);
  const maxSessWeek = selectedExp ? Math.floor((selectedExp.max_weekly_hours || 8) / (selectedExp.session_duration_hours || 3)) : 2;
  const availCount = Object.keys(availDates).length;

  if (loading) return <div className="flex justify-center py-12"><Loader2 className="h-6 w-6 animate-spin text-forest-800" /></div>;

  return (
    <div className="space-y-6">
      {/* Add + List row */}
      <div className="grid grid-cols-1 lg:grid-cols-3 gap-4">
        {/* Add form */}
        <Card className="bg-white rounded-2xl border-stone-200 shadow-sm">
          <CardHeader className="pb-3">
            <CardTitle className="font-outfit text-base text-forest-800">{language === "de" ? "Neuer Experimenter" : "New Experimenter"}</CardTitle>
          </CardHeader>
          <CardContent className="space-y-3">
            <Input placeholder="Name" value={name} onChange={(e) => setName(e.target.value)} className="bg-stone-50 rounded-xl" data-testid="exp-name-input" />
            <div className="grid grid-cols-2 gap-2">
              <div>
                <Label className="text-xs">{language === "de" ? "Max h/Woche" : "Max h/week"}</Label>
                <Input type="number" min="1" max="40" value={maxHours} onChange={(e) => setMaxHours(e.target.value)} className="mt-1 bg-stone-50 rounded-xl" />
              </div>
              <div>
                <Label className="text-xs">{language === "de" ? "Session (h)" : "Session (h)"}</Label>
                <Input type="number" min="0.5" max="8" step="0.5" value={sessionDur} onChange={(e) => setSessionDur(e.target.value)} className="mt-1 bg-stone-50 rounded-xl" />
              </div>
            </div>
            <Button className="w-full bg-forest-800 hover:bg-forest-900 rounded-full text-sm" onClick={handleAdd} disabled={!name.trim() || adding} data-testid="add-exp-btn">
              {adding ? <Loader2 className="h-4 w-4 animate-spin mr-1" /> : <Plus className="h-4 w-4 mr-1" />}
              {language === "de" ? "Hinzufügen" : "Add"}
            </Button>
          </CardContent>
        </Card>

        {/* Experimenter list */}
        <Card className="bg-white rounded-2xl border-stone-200 shadow-sm lg:col-span-2">
          <CardHeader className="pb-3">
            <CardTitle className="font-outfit text-base text-forest-800">{language === "de" ? "Experimenter" : "Experimenters"} ({experimenters.length})</CardTitle>
          </CardHeader>
          <CardContent>
            {experimenters.length === 0 ? (
              <p className="text-stone-500 text-center py-4 text-sm">{language === "de" ? "Keine Experimenter" : "No experimenters"}</p>
            ) : (
              <div className="flex flex-wrap gap-2">
                {experimenters.map(exp => (
                  <div key={exp.id}
                    className={`flex items-center gap-2 px-3 py-2 rounded-xl border cursor-pointer transition-all ${selectedExpId === exp.id ? "bg-forest-800 text-white border-forest-800" : "bg-stone-50 border-stone-200 hover:border-forest-300"}`}
                    onClick={() => setSelectedExpId(exp.id)}
                  >
                    <User className="h-3.5 w-3.5" />
                    <span className="text-sm font-medium">{exp.name}</span>
                    <Badge className={`text-[10px] ${selectedExpId === exp.id ? "bg-white/20 text-white" : "bg-stone-200 text-stone-600"}`}>
                      {exp.max_weekly_hours}h | {Math.floor(exp.max_weekly_hours / exp.session_duration_hours)}x{exp.session_duration_hours}h
                    </Badge>
                    <button onClick={(e) => { e.stopPropagation(); handleDelete(exp.id); }} className="ml-1 opacity-60 hover:opacity-100">
                      <Trash2 className="h-3 w-3" />
                    </button>
                  </div>
                ))}
              </div>
            )}
          </CardContent>
        </Card>
      </div>

      {/* Monthly availability calendar */}
      {selectedExp && (
        <Card className="bg-white rounded-2xl border-stone-200 shadow-sm">
          <CardHeader>
            <div className="flex items-center justify-between flex-wrap gap-3">
              <div>
                <CardTitle className="font-outfit text-base text-forest-800">
                  {language === "de" ? "Monatliche Verfügbarkeit" : "Monthly Availability"}: <span className="text-forest-600">{selectedExp.name}</span>
                </CardTitle>
                <CardDescription className="text-xs mt-1">
                  {language === "de"
                    ? `Klicken Sie auf Tage, an denen ${selectedExp.name} verfügbar ist. Max ${maxSessWeek} Sessions/Woche (${selectedExp.max_weekly_hours}h ÷ ${selectedExp.session_duration_hours}h).`
                    : `Click days when ${selectedExp.name} is available. Max ${maxSessWeek} sessions/week (${selectedExp.max_weekly_hours}h ÷ ${selectedExp.session_duration_hours}h).`}
                </CardDescription>
              </div>
              <div className="flex items-center gap-2">
                <Button variant="outline" size="sm" onClick={prevMonth} className="h-7 w-7 p-0 rounded-lg"><ChevronLeft className="h-3 w-3" /></Button>
                <span className="text-sm font-medium min-w-[120px] text-center">{monthNames[calMonth - 1]} {calYear}</span>
                <Button variant="outline" size="sm" onClick={nextMonth} className="h-7 w-7 p-0 rounded-lg"><ChevronRight className="h-3 w-3" /></Button>
              </div>
            </div>
            {/* Default time range */}
            <div className="flex items-center gap-3 mt-3">
              <Label className="text-xs text-stone-500">{language === "de" ? "Standardzeit:" : "Default time:"}</Label>
              <Input type="time" value={defaultStart} onChange={(e) => setDefaultStart(e.target.value)} className="h-7 w-28 text-xs bg-stone-50 rounded-lg" />
              <span className="text-xs text-stone-400">–</span>
              <Input type="time" value={defaultEnd} onChange={(e) => setDefaultEnd(e.target.value)} className="h-7 w-28 text-xs bg-stone-50 rounded-lg" />
              <Badge className="bg-forest-100 text-forest-800 text-xs">{availCount} {language === "de" ? "Tage" : "days"}</Badge>
            </div>
          </CardHeader>
          <CardContent>
            <div className="grid grid-cols-7 gap-1 mb-1">
              {dayHeaders.map(d => <div key={d} className="text-center text-xs font-medium text-stone-500 py-1">{d}</div>)}
            </div>
            {buildWeeks().map((week, wIdx) => (
              <div key={wIdx} className="grid grid-cols-7 gap-1 mb-1">
                {week.map((day, dIdx) => {
                  const inMonth = isSameMonth(day, new Date(calYear, calMonth - 1));
                  const dateStr = format(day, "yyyy-MM-dd");
                  const isAvail = !!availDates[dateStr];
                  const isWeekend = day.getDay() === 0 || day.getDay() === 6;
                  const isPast = day < new Date(new Date().setHours(0,0,0,0));
                  return (
                    <button key={dIdx} type="button"
                      disabled={!inMonth || isPast}
                      onClick={() => inMonth && !isPast && toggleDate(dateStr)}
                      className={`min-h-[48px] rounded-lg p-1.5 border text-left transition-all
                        ${!inMonth ? "bg-transparent border-transparent cursor-default" :
                          isPast ? "bg-stone-50 border-stone-100 cursor-not-allowed opacity-40" :
                          isAvail ? "bg-forest-100 border-forest-400 hover:bg-forest-200" :
                          isWeekend ? "bg-stone-50 border-stone-100 hover:border-stone-300" :
                          "bg-white border-stone-200 hover:border-forest-300"}`}
                    >
                      <p className={`text-xs font-medium ${!inMonth ? "text-transparent" : isAvail ? "text-forest-800" : "text-stone-500"}`}>
                        {format(day, "d")}
                      </p>
                      {isAvail && inMonth && (
                        <p className="text-[9px] text-forest-600 mt-0.5">{availDates[dateStr].start_time}-{availDates[dateStr].end_time}</p>
                      )}
                    </button>
                  );
                })}
              </div>
            ))}
            <div className="flex justify-end mt-3">
              <Button onClick={saveAvailability} disabled={savingAvail} className="bg-forest-800 hover:bg-forest-900 text-white rounded-full" data-testid="save-avail-btn">
                {savingAvail ? <Loader2 className="h-4 w-4 animate-spin mr-1" /> : <Check className="h-4 w-4 mr-1" />}
                {language === "de" ? "Verfügbarkeit speichern" : "Save Availability"} ({availCount} {language === "de" ? "Tage" : "days"})
              </Button>
            </div>
          </CardContent>
        </Card>
      )}
    </div>
  );
}
